import { useState, useEffect } from "react";
import { API } from "../../App";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableRow,
} from "../../components/ui/table";
import { Plus, Edit, Trash2, Check, X, Loader2, Link2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { BilingualInput } from "../../components/BilingualField";

export default function AdminFooterLinks() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editingLink, setEditingLink] = useState(null);

  const SECTIONS = [
    { id: "explore", label: t("admin_footer:section_explore", { defaultValue: "Explore" }) },
    { id: "for_venues", label: t("admin_footer:section_for_venues", { defaultValue: "For Venues" }) },
    { id: "company", label: t("admin_footer:section_company", { defaultValue: "Company" }) },
  ];

  useEffect(() => { fetchLinks(); }, []);

  const fetchLinks = async () => {
    try {
      const res = await axios.get(`${API}/admin/footer-links`);
      setLinks(res.data);
    } catch (error) { console.error(error); }
    finally { setLoading(false); }
  };

  const handleDelete = async (linkId) => {
    if (!window.confirm(t("admin_footer:delete_confirm", { defaultValue: "Delete this footer link?" }))) return;
    try {
      await axios.delete(`${API}/admin/footer-links/${linkId}`);
      setLinks(links.filter((l) => l.link_id !== linkId));
      toast.success(t("admin_footer:deleted_toast", { defaultValue: "Link deleted" }));
    } catch (error) { toast.error(t("admin_footer:failed_toast", { defaultValue: "Failed" })); }
  };

  const groupedLinks = SECTIONS.map((s) => ({
    ...s,
    links: links.filter((l) => l.section === s.id),
  }));

  const titleFor = (l) => (lang === "el" && l.title_el) || l.title;

  return (
    <div data-testid="admin-footer-links-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-medium text-foreground flex items-center gap-2"><Link2 className="h-6 w-6 text-primary" />{t("admin_footer:title")}</h1>
        <Dialog open={showCreate} onOpenChange={setShowCreate}>
          <DialogTrigger asChild>
            <Button data-testid="footer-link-create-btn">
              <Plus className="h-4 w-4 mr-2" /> {t("admin_footer:add_link")}
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t("admin_footer:add_dialog_title", { defaultValue: "Add Footer Link" })}</DialogTitle>
            </DialogHeader>
            <LinkForm
              sections={SECTIONS}
              onSuccess={(l) => { setLinks([...links, l]); setShowCreate(false); }}
            />
          </DialogContent>
        </Dialog>
      </div>

      {editingLink && (
        <Dialog open={!!editingLink} onOpenChange={() => setEditingLink(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t("admin_footer:edit_dialog_title", { defaultValue: "Edit Footer Link" })}</DialogTitle>
            </DialogHeader>
            <LinkForm
              link={editingLink}
              sections={SECTIONS}
              onSuccess={(l) => {
                setLinks(links.map((x) => x.link_id === l.link_id ? l : x));
                setEditingLink(null);
              }}
            />
          </DialogContent>
        </Dialog>
      )}

      {loading ? <div className="animate-pulse bg-muted h-64 rounded-xl" /> : (
        <div className="space-y-6">
          {groupedLinks.map((section) => (
            <div key={section.id} className="bg-card rounded-xl border border-border overflow-hidden">
              <div className="px-5 py-3 border-b border-border bg-muted/30">
                <h3 className="font-medium text-foreground text-sm">{section.label}</h3>
              </div>
              {section.links.length > 0 ? (
                <Table>
                  <TableBody>
                    {section.links.map((link) => (
                      <TableRow key={link.link_id} data-testid={`footer-link-row-${link.link_id}`}>
                        <TableCell className="font-medium">{titleFor(link)}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">{link.url}</TableCell>
                        <TableCell>
                          {link.is_active ? <Check className="h-4 w-4 text-green-600" /> : <X className="h-4 w-4 text-red-500" />}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button variant="ghost" size="sm" onClick={() => setEditingLink(link)} data-testid={`edit-footer-link-${link.link_id}`}>
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDelete(link.link_id)} data-testid={`delete-footer-link-${link.link_id}`}>
                              <Trash2 className="h-4 w-4 text-muted-foreground hover:text-destructive" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <p className="px-5 py-4 text-sm text-muted-foreground">{t("admin_footer:no_links", { defaultValue: "No links in this section" })}</p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function LinkForm({ link, sections, onSuccess }) {
  const { t } = useTranslation();
  const isEdit = !!link;
  const [form, setForm] = useState({
    title: link?.title || "",
    title_el: link?.title_el || "",
    url: link?.url || "",
    section: link?.section || "company",
    sort_order: link?.sort_order || 0,
    is_active: link?.is_active ?? true,
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = isEdit
        ? await axios.put(`${API}/admin/footer-links/${link.link_id}`, form)
        : await axios.post(`${API}/admin/footer-links`, form);
      toast.success(isEdit ? t("admin_footer:updated_toast", { defaultValue: "Link updated" }) : t("admin_footer:created_toast", { defaultValue: "Link created" }));
      onSuccess(res.data);
    } catch (error) { toast.error(error.response?.data?.detail || t("admin_footer:failed_toast", { defaultValue: "Failed" })); }
    finally { setLoading(false); }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <BilingualInput
        label={t("admin_footer:field_title", { defaultValue: "Title" })}
        value={form.title}
        valueEl={form.title_el}
        onChange={(e) => setForm({ ...form, title: e.target.value })}
        onChangeEl={(e) => setForm({ ...form, title_el: e.target.value })}
        required
        data-testid="footer-link-title-input"
      />
      <div>
        <Label>{t("admin_footer:field_url", { defaultValue: "URL" })}</Label>
        <Input
          value={form.url}
          onChange={(e) => setForm({ ...form, url: e.target.value })}
          required
          className="mt-1"
          placeholder="/page/about or https://..."
          data-testid="footer-link-url-input"
        />
      </div>
      <div>
        <Label>{t("admin_footer:field_section", { defaultValue: "Section" })}</Label>
        <Select value={form.section} onValueChange={(v) => setForm({ ...form, section: v })}>
          <SelectTrigger className="mt-1" data-testid="footer-link-section-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            {sections.map((s) => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>{t("admin_footer:field_sort_order", { defaultValue: "Sort Order" })}</Label>
          <Input
            type="number"
            value={form.sort_order}
            onChange={(e) => setForm({ ...form, sort_order: parseInt(e.target.value) || 0 })}
            className="mt-1"
          />
        </div>
        <div>
          <Label>{t("admin_footer:field_active", { defaultValue: "Active" })}</Label>
          <Select value={form.is_active ? "active" : "inactive"} onValueChange={(v) => setForm({ ...form, is_active: v === "active" })}>
            <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="active">{t("admin_footer:option_active", { defaultValue: "Active" })}</SelectItem>
              <SelectItem value="inactive">{t("admin_footer:option_inactive", { defaultValue: "Inactive" })}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <Button type="submit" disabled={loading} className="w-full" data-testid="footer-link-submit-btn">
        {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
        {isEdit ? t("admin_footer:update_btn", { defaultValue: "Update" }) : t("admin_footer:create_btn", { defaultValue: "Create" })}
      </Button>
    </form>
  );
}
